// =============================================================================
// BP-05-03: Calendar Frontend - AgendaPage
// =============================================================================

import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, isToday, isTomorrow, isThisWeek, isThisMonth, parseISO } from 'date-fns';
import { Calendar, Plus, ChevronRight, Filter } from 'lucide-react';
import { AppointmentCard } from '../components';
import { useAgenda, useCalendarStore } from '../hooks';
import { type AppointmentSummary } from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface GroupedAppointments {
  label: string;
  appointments: AppointmentSummary[];
}

// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------

function groupAppointmentsByDate(appointments: AppointmentSummary[]): GroupedAppointments[] {
  const groups: Map<string, AppointmentSummary[]> = new Map();

  appointments.forEach((apt) => {
    const date = parseISO(apt.startsAt);
    let label: string;

    if (isToday(date)) {
      label = 'Today';
    } else if (isTomorrow(date)) {
      label = 'Tomorrow';
    } else if (isThisWeek(date)) {
      label = format(date, 'EEEE');
    } else if (isThisMonth(date)) {
      label = format(date, 'EEEE, MMMM d');
    } else {
      label = format(date, 'MMMM yyyy');
    }

    const existing = groups.get(label) || [];
    groups.set(label, [...existing, apt]);
  });

  return Array.from(groups.entries()).map(([label, appointments]) => ({
    label,
    appointments: appointments.sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
    ),
  }));
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function AgendaPage() {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const navigate = useNavigate();

  const { selectedChildId, openForm } = useCalendarStore();

  // Fetch agenda
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAgenda(familyId, { childId: selectedChildId || undefined });

  // Flatten and group appointments
  const groupedAppointments = useMemo(() => {
    if (!data?.pages) return [];

    const allAppointments = data.pages.flatMap((page) => page.appointments);
    return groupAppointmentsByDate(allAppointments);
  }, [data]);

  const totalCount = data?.pages[0]?.appointments.length || 0;

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={`/family/${familyId}/calendar`}
                className="p-2 hover:bg-cream rounded-lg transition-colors"
              >
                <Calendar className="w-5 h-5 text-navy" />
              </Link>
              <div>
                <h1 className="text-2xl font-display font-semibold text-navy">
                  Agenda
                </h1>
                <p className="text-sm text-warm-gray mt-0.5">
                  Upcoming appointments
                </p>
              </div>
            </div>
            <button
              onClick={() => openForm()}
              className="flex items-center gap-2 px-4 py-2.5 bg-sage text-white rounded-lg font-medium hover:bg-sage-dark transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Appointment
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
          </div>
        ) : groupedAppointments.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
            <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-sage" />
            </div>
            <h3 className="text-lg font-display font-semibold text-navy mb-2">
              No upcoming appointments
            </h3>
            <p className="text-warm-gray mb-6">
              Schedule your first appointment to get started
            </p>
            <button
              onClick={() => openForm()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-lg font-medium hover:bg-sage-dark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Schedule Appointment
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {groupedAppointments.map((group) => (
              <div key={group.label}>
                {/* Group Header */}
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-lg font-display font-semibold text-navy">
                    {group.label}
                  </h2>
                  <span className="px-2 py-0.5 bg-sage/10 text-sage text-sm font-medium rounded-full">
                    {group.appointments.length}
                  </span>
                </div>

                {/* Appointments */}
                <div className="space-y-3">
                  {group.appointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      showDate={group.label !== 'Today' && group.label !== 'Tomorrow'}
                      onClick={() =>
                        navigate(`/family/${familyId}/appointments/${appointment.id}`)
                      }
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Load More */}
            {hasNextPage && (
              <div className="text-center py-4">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-navy rounded-lg font-medium hover:bg-cream transition-colors shadow-soft disabled:opacity-50"
                >
                  {isFetchingNextPage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sage" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default AgendaPage;
